import {
  GetClipsSort,
  QueryGetClipsByCampaignUuidArgs,
  Campaign,
  QueryGetDistinctLabelNamesByCampaignArgs,
} from '../../../gql/types'
import { useQuery } from '@apollo/client'
import {
  GET_CLIPS_BY_CAMPAIGN_UUID,
  GetClipsByCampaignUuidResponse,
} from '../../../gql'
import {
  GET_DISTINCT_LABEL_NAMES_BY_CAMPAIGN,
  QueryGetDistinctLabelNamesByCampaignResponse,
} from '../../../gql/labels'
import { LIMIT_DEFAULT } from '../../../utils/constants'
import { Loader, Pagination } from '../../../components'
import { ContentGridPublic } from '../components/ContentGridPublic'
import { useState, useEffect } from 'react'
import {
  Badge,
  BadgeSize,
  BadgeStyle,
  BadgeType,
} from '../../../components/Badge'

interface CampaignContentPublicProps {
  campaignUuid: string
  campaign: Campaign
}

export const CampaignContentPublic = ({
  campaignUuid,
  campaign,
}: CampaignContentPublicProps) => {
  const limit = LIMIT_DEFAULT
  const [page, setPage] = useState(0)
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])

  const { loading, error, data, refetch } = useQuery<
    GetClipsByCampaignUuidResponse,
    QueryGetClipsByCampaignUuidArgs
  >(GET_CLIPS_BY_CAMPAIGN_UUID, {
    variables: {
      uuid: campaignUuid,
      options: {
        query: '',
        filters: {
          labelNames: selectedLabels.length > 0 ? selectedLabels : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: page * limit,
      },
    },
  })

  const { data: labelData, loading: labelLoading } = useQuery<
    QueryGetDistinctLabelNamesByCampaignResponse,
    QueryGetDistinctLabelNamesByCampaignArgs
  >(GET_DISTINCT_LABEL_NAMES_BY_CAMPAIGN, {
    variables: {
      campaignId: campaign.id,
    },
  })

  const labelNames = labelData?.getDistinctLabelNamesByCampaign || []

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [selectedLabels])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const toggleLabelFilter = (label: string) => {
    setSelectedLabels((prev) => {
      if (prev.includes(label)) {
        return prev.filter((l) => l !== label)
      } else {
        return [...prev, label]
      }
    })
  }

  if (error) {
    return <p>There has been an error. Try refreshing the page</p>
  }

  if (loading && !data?.getClipsByCampaignUuid) {
    return <Loader />
  }

  if (!loading && !data?.getClipsByCampaignUuid) {
    return <p>Campaign does not exist.</p>
  }

  return (
    <div className='pb-4 w-auto h-fit'>
      {labelNames.length > 0 && (
        <div className='flex flex-wrap gap-2 my-4 px-2'>
          {labelNames.map((label, index) => (
            <Badge
              key={index}
              type={
                selectedLabels.includes(label)
                  ? BadgeType.indigo
                  : BadgeType.gray
              }
              style={BadgeStyle.fill}
              size={BadgeSize.medium}
              text={label}
              className={`cursor-pointer ${selectedLabels.includes(label) ? 'ring-2 ring-indigo-500' : ''}`}
              onClick={() => toggleLabelFilter(label)}
            />
          ))}

          {selectedLabels.length > 0 && (
            <Badge
              type={BadgeType.error}
              style={BadgeStyle.outline}
              size={BadgeSize.medium}
              text='Clear all filters'
              className='cursor-pointer ml-2'
              onClick={() => setSelectedLabels([])}
            />
          )}
        </div>
      )}

      <div className='mt-4 overflow-x-auto'>
        <div className='inline-block min-w-full align-middle'>
          <div>
            <ContentGridPublic
              clips={data?.getClipsByCampaignUuid?.items || []}
              loading={loading}
              labelNames={labelNames}
            />

            {data?.getClipsByCampaignUuid?.pagination && (
              <div className='mt-4'>
                <Pagination
                  pagination={{
                    ...data.getClipsByCampaignUuid.pagination,
                    pageNumber: page,
                    hitsPerPage: limit,
                  }}
                  setPage={handlePageChange}
                  className='bg-white'
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
